import { alpha, Chip, type ChipProps } from '@mui/material';
import { useTheme } from '@parkdna/ui/theme';

import type { SupportStatus } from '../types';

type StatusPaletteKey = 'inProgress' | 'warning' | 'success' | 'textSecondary';

interface StatusPresentation {
  label: string;
  palette: StatusPaletteKey;
}

// Labels are written from the CLIENT's (requester's) perspective, not the
// agent's — the raw Zendesk status stays the source of truth, we only relabel
// it for this client-facing portal. Notably `pending` (agent waiting on the
// requester) becomes "Awaiting Your Reply", matching Zendesk's own Help Center.
// Status is conveyed by the label TEXT (not color alone) for color-independence.
// Colors map to theme.palette.* only — never hardcoded hex (whitelabel).
const STATUS_PRESENTATION: Record<SupportStatus, StatusPresentation> = {
  new: { label: 'In Progress', palette: 'inProgress' },
  open: { label: 'In Progress', palette: 'inProgress' },
  pending: { label: 'Awaiting Your Reply', palette: 'warning' },
  solved: { label: 'Resolved', palette: 'success' },
  closed: { label: 'Closed', palette: 'textSecondary' },
};

export interface SupportStatusChipProps
  extends Omit<ChipProps, 'label' | 'color'> {
  status: SupportStatus;
}

export function SupportStatusChip({
  status,
  size = 'small',
  ...chipProps
}: SupportStatusChipProps) {
  const [theme] = useTheme();
  const { label, palette } = STATUS_PRESENTATION[status];

  const mainColor =
    palette === 'textSecondary'
      ? theme.palette.text.secondary
      : theme.palette[palette].main;

  return (
    <Chip
      {...chipProps}
      size={size}
      label={label}
      sx={{
        fontWeight: 600,
        color: mainColor,
        // Tint background derived from the same palette color so the pill
        // adapts to any customer's branding.
        backgroundColor: alpha(mainColor, 0.12),
        ...chipProps.sx,
      }}
    />
  );
}

export default SupportStatusChip;
